// opervex-frontend/src/pages/Accounting/FiscalPeriod.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Form, InputGroup, Card, Button } from "react-bootstrap";
import { FaSearch, FaPlus, FaSave, FaPencilAlt } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import MaskedFormControl from "react-bootstrap-maskedinput";

import { useAuth } from "../../contexts/AuthContext";
import { SearchHelpModal } from "../../utils/SearchHelp";
import useOpervex from "../../hooks/useOpervex";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";

const rolePageRequire = "FISCAL_PERIOD";

var dataDefault = {
  period: "",
  startDate: null,
  endDate: null,
  deadline: null,
  movement: false,
  closed: false,
};

const actionsDefault = {
  create: true,
  change: true,
  delete: true,
  save: true,
};

const modeDefault = {
  create: "Create",
  change: "Change",
  read: "Read",
};

const FiscalPeriod = () => {
  const { roles, loadingGlobal } = useAuth();
  const [disabledField, setDisabledField] = useState(true);
  const [actions, setActions] = useState(actionsDefault);
  const [mode, setMode] = useState("Read");
  const [data, setData] = useState(dataDefault);
  const { id } = useParams();
  const OpervexAPI = useOpervex();

  const fetchData = async () => {
    commandAction({ ...actionsDefault, create: false });

    if (id) await searchAPI(id);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loadingGlobal) {
    return <div>Carregando...</div>;
  }

  const commandAction = (actions) => {
    const hasPermission = roles.find((role) => role.command === rolePageRequire);

    if (!hasPermission) {
      setActions(actionsDefault);
      return;
    }

    if (!actions.create) actions.create = !hasPermission.actions.some((action) => action.key === "Create");
    if (!actions.change) actions.change = !hasPermission.actions.some((action) => action.key === "Change");

    setActions({ ...actions });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    var newData = { ...data, [name]: type === "checkbox" ? checked : value };

    if (name === "period") {
      const maskComplete = /^\d{2}\.\d{4}$/;

      if (maskComplete.test(value)) {
        const [monthStr, yearStr] = value.split(".");
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);

        if (month >= 1 && month <= 12) {
          newData.startDate = new Date(year, month - 1, 1).toISOString().split("T")[0];
          newData.endDate = new Date(year, month, 0).toISOString().split("T")[0];
          newData.deadline = new Date(year, month, 5).toISOString().split("T")[0];
        }
      }
    }

    setData(newData);
  };

  // CRUD Handlers
  const handleButtonCreate = () => {
    setData(dataDefault);
    commandAction({ ...actionsDefault, save: false });
    setMode(modeDefault.create);
    setDisabledField(false);
  };

  const handleButtonCancel = () => {
    if (data.period === "") {
      setData(dataDefault);
      setDisabledField(true);
      setMode(modeDefault.read);
      commandAction({ ...actionsDefault, create: false });
    } else {
      searchAPI(data.period);
    }
  };

  const handleButtonSave = async () => {
    var body = { ...data };

    body.period = body.period.replaceAll(".", "");

    if (mode === modeDefault.change) {
      const result = await OpervexAPI.Accounting.FiscalPeriods.update(body.period, body);
      if (result.errors) {
        await ConfirmService.show("Aviso", result.errors, true);
        return;
      }

      alert("Registro atualizado com sucesso!");
    } else {
      const result = await OpervexAPI.Accounting.FiscalPeriods.create(body);
      if (result.errors) {
        await ConfirmService.show("Aviso", result.errors, true);
        return;
      }

      alert("Registro criado com sucesso!");
    }

    await searchAPI(body.period);
  };

  const handleButtonSearch = async () => {
    const item = await SearchHelpModal({
      urlData: "/Accounting/FiscalPeriod/SearchHelp",
      schema: await OpervexAPI.Accounting.FiscalPeriods.schema(),
    });

    if (item && item.period) await searchAPI(item.period);
  };

  const handleButtonChange = async () => {
    setDisabledField(false);
    setMode(modeDefault.change);
    commandAction({ ...actionsDefault, save: false });
  };

  const searchAPI = async (id) => {
    setDisabledField(true);
    setMode(modeDefault.read);

    if (!id) return;

    var result = await OpervexAPI.Accounting.FiscalPeriods.findId(id);

    if (result && !result.error) {
      setData(result);
      commandAction({ ...actionsDefault, create: false, change: false });
    } else {
      setData(dataDefault);
      commandAction({ ...actionsDefault, create: false });
      alert(result.error);
    }
  };

  return (
    <>
      <Container className="mt-2">
        <Form onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}>
          {/* ----- Header / Additional -----*/}
          <Card className="mb-2">
            <Card.Header>
              <Row className="d-flex justify-content-between">
                <Col md={8}>
                  <h5>Cabeçalho</h5>
                </Col>
                <Col md={4} className="text-end">
                  <Button variant="success" size="sm" className="me-2" onClick={handleButtonCreate} hidden={actions.create}>
                    <FaPlus /> Novo
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonChange} hidden={actions.change}>
                    <FaPencilAlt /> Alterar
                  </Button>
                  <Button variant="danger" size="sm" className="me-2" onClick={handleButtonCancel} hidden={actions.save}>
                    <MdOutlineCancel /> Cancelar
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonSave} hidden={actions.save}>
                    <FaSave /> Salvar
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Período</Form.Label>
                    <InputGroup>
                      <MaskedFormControl
                        type="text"
                        size="sm"
                        mask="11.1111"
                        name="period"
                        value={data.period}
                        onChange={handleChange}
                        disabled={!(mode === modeDefault.create)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault();
                        }}
                      />

                      <InputGroup.Text size="sm" type="button" onClick={handleButtonSearch}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}></Col>
                <Col md={2} className="d-flex align-items-center">
                  <Form.Check type="checkbox" id="closed" checked={data.closed} name="closed" label="Fechado" disabled={true} />
                </Col>
                <Col md={2} className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="movement"
                    checked={data.movement}
                    name="movement"
                    label="Movimentação"
                    disabled={true}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          {/* ----- Additional -----*/}
          <Card className="mb-2">
            <Card.Header>
              <h5>Informações Adicionais</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Data Início</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      size="sm"
                      value={data.startDate || ""}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Data Fim</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      size="sm"
                      value={data.endDate || ""}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Data Limite</Form.Label>
                    <Form.Control
                      type="date"
                      name="deadline"
                      size="sm"
                      value={data.deadline || ""}
                      onChange={handleChange}
                      disabled={disabledField}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}></Col>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </Container>
    </>
  );
};

export default FiscalPeriod;
